import * as React from "react";
import AppBar from "@mui/material/AppBar";
import AppIcon from "./AppIcon";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { getUrl } from "@aws-amplify/storage";
import { fetchAuthSession, signOut } from "@aws-amplify/auth";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useState, useEffect } from "react";
import { Divider, Select } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthInfo } from "../helpers/useAuthInfo";
import {
  CGI_GRADIENT_B,
  CGI_GREY,
  CGI_RED,
  CGI_WHITE,
  congnitoGroups,
  languages,
} from "../helpers/enums";

export default function MenuBar({ language, setLanguage }) {
  const profileImageId = "Profilbild";
  const { t, i18n } = useTranslation();
  let location = useLocation();

  const auth = useAuthenticator(); // nur einmal
  const user = auth.user;
  const isAuthenticated = !!user;

  const { email, userGroups } = useAuthInfo();

  const [profileImg, setProfileImg] = useState("");
  const [, setImgError] = useState(false);

  const pages = [
    {
      title: t("catalogue"),
      redirectTo: "/catalogue",
      admin: false,
    },
    {
      title: t("assessmentsAndEvaluations"),
      redirectTo: "/assessmentsEvaluations",
      admin: false,
    },
    { title: t("benchmark"), redirectTo: "/benchmark", admin: false },
    {
      title: t("public"),
      redirectTo: "/managePublicAssessments",
      admin: true,
    },
  ];

  const adminPages = [
    {
      title: t("manageAccounts"),
      redirectTo: "/manageAccounts",
      admin: true,
    },
    {
      title: t("manageAssessments"),
      redirectTo: "/manageAssessments",
      admin: false,
    },
    {
      title: t("manageCustomers"),
      redirectTo: "/manageCustomers",
      admin: true,
    },
  ];

  const filteredAdminPages = adminPages.filter(
    (page) => !page.admin || userGroups?.includes(congnitoGroups.ADMIN)
  );

  const navigate = useNavigate();

  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [anchorElAdmin, setAnchorElAdmin] = React.useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleOpenAdminMenu = (event) => {
    setAnchorElAdmin(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const handleCloseAdminMenu = () => {
    setAnchorElAdmin(null);
  };

  const handleSignOut = async () => {
    handleCloseUserMenu();
    try {
      await signOut();
      setProfileImg("");
      setImgError(false);
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Sign out failed", error);
    }
  };

  useEffect(() => {
    let mounted = true;
    let lastUrl = null;

    async function loadProfileImage() {
      if (!isAuthenticated) {
        if (mounted) {
          setProfileImg("");
          setImgError(false);
        }
        return;
      }

      try {
        await fetchAuthSession();
      } catch (err) {
        console.debug("fetchAuthSession failed (skip protected image)", err);
        if (mounted) {
          setProfileImg("");
          setImgError(false);
        }
        return;
      }

      try {
        const res = await getUrl({
          key: profileImageId,
          options: { validateObjectExistence: true, accessLevel: "protected" },
        });

        const url = res?.url ?? "";

        try {
          const parsed = new URL(url);
          if (
            parsed.hostname === window.location.hostname &&
            (parsed.pathname === "/" || parsed.pathname === "")
          ) {
            if (mounted) {
              setProfileImg("");
              setImgError(false);
            }
            return;
          }
        } catch (e) {
          console.debug("Invalid profile image URL", e);
        }

        if (mounted && url !== lastUrl) {
          lastUrl = url;
          setProfileImg(url || "");
          setImgError(false);
        }
      } catch (err) {
        console.debug(err?.message ?? err);
        if (mounted) {
          setProfileImg("");
          setImgError(false);
        }
      }
    }

    loadProfileImage();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  return (
    <>
      {/* {route !== "authenticated" && (
        <Select
          sx={{
            borderRadius: 20,
            height: 30,
            position: "absolute",
            top: 30,
            left: 20,
          }}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          <MenuItem value={languages.DE}>{languages.DE}</MenuItem>
          <MenuItem value={languages.EN}>{languages.EN}</MenuItem>
        </Select>
      )} */}
      {isAuthenticated && (
        <AppBar
          sx={{
            position: "sticky",
            top: 0,
            height: 75,
            background: CGI_GRADIENT_B,
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ height: 75 }}>
              {/* Small Page */}
              <Box sx={{ display: { xs: "flex", lg: "none" } }}>
                <IconButton
                  size="large"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  sx={{ color: CGI_WHITE }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    display: {
                      xs: "block",
                      lg: "none",
                      "& .MuiPaper-root": {
                        backgroundColor: CGI_RED,
                      },
                    },
                  }}
                >
                  {filteredAdminPages.map((page, index) => (
                    <Box key={index}>
                      <MenuItem
                        onClick={() => {
                          handleCloseNavMenu();
                          navigate(page.redirectTo);
                        }}
                      >
                        <Typography
                          textAlign="center"
                          sx={{
                            userSelect: "none",
                            fontWeight:
                              location.pathname === page.redirectTo
                                ? 500
                                : "normal",
                            color:
                              location.pathname === page.redirectTo
                                ? CGI_GREY
                                : CGI_WHITE,
                          }}
                        >
                          {page.title}
                        </Typography>
                      </MenuItem>
                    </Box>
                  ))}
                  {pages
                    .filter(
                      (x) =>
                        !x.admin || userGroups.includes(congnitoGroups.ADMIN)
                    )
                    .map((page, index) => (
                      <Box key={index}>
                        <MenuItem
                          onClick={() => {
                            handleCloseNavMenu();
                            navigate(page.redirectTo);
                          }}
                        >
                          <Typography
                            textAlign="center"
                            sx={{
                              fontWeight:
                                location.pathname === page.redirectTo
                                  ? 500
                                  : "normal",
                              color:
                                location.pathname === page.redirectTo
                                  ? CGI_GREY
                                  : CGI_WHITE,
                            }}
                          >
                            {page.title}
                          </Typography>
                        </MenuItem>
                      </Box>
                    ))}
                </Menu>
              </Box>
              <AppIcon />
              {/* Big Page */}
              <Box sx={{ flexGrow: 1, display: { xs: "none", lg: "flex" } }}>
                {filteredAdminPages.length > 0 && (
                  <>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Divider
                        sx={{ bgcolor: "#fff", my: 2.5 }}
                        flexItem
                        orientation="vertical"
                      />
                      <Button
                        onClick={handleOpenAdminMenu}
                        sx={{
                          my: 2,
                          display: "block",
                          px: 3,
                          fontWeight: adminPages.find(
                            (e) => e.redirectTo === location.pathname
                          )
                            ? 500
                            : "normal",
                          color: adminPages.find(
                            (e) => e.redirectTo === location.pathname
                          )
                            ? CGI_GREY
                            : CGI_WHITE,
                        }}
                      >
                        Management
                      </Button>
                    </Box>
                    <Menu
                      id="menu-appbar"
                      anchorEl={anchorElAdmin}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      open={Boolean(anchorElAdmin)}
                      onClose={handleCloseAdminMenu}
                      sx={{
                        "& .MuiPaper-root": {
                          backgroundColor: CGI_RED,
                        },
                        display: { xs: "none", lg: "block" },
                      }}
                    >
                      {adminPages
                        .filter(
                          (x) =>
                            !x.admin ||
                            userGroups.includes(congnitoGroups.ADMIN)
                        )
                        .map((page, index) => (
                          <Box key={index}>
                            <MenuItem
                              onClick={() => {
                                handleCloseAdminMenu();
                                navigate(page.redirectTo);
                              }}
                            >
                              <Typography
                                textAlign="center"
                                sx={{
                                  fontWeight:
                                    location.pathname === page.redirectTo
                                      ? 500
                                      : "normal",
                                  color:
                                    location.pathname === page.redirectTo
                                      ? CGI_GREY
                                      : CGI_WHITE,
                                }}
                              >
                                {page.title}
                              </Typography>
                            </MenuItem>
                          </Box>
                        ))}
                    </Menu>
                  </>
                )}
                {pages
                  .filter(
                    (x) => !x.admin || userGroups.includes(congnitoGroups.ADMIN)
                  )
                  .map((page, index) => (
                    <Box key={index}>
                      <Box sx={{ display: "flex", flexDirection: "row" }}>
                        <Divider
                          sx={{ bgcolor: "#fff", my: 2.5 }}
                          flexItem
                          orientation="vertical"
                        />
                        <Button
                          onClick={() => {
                            handleCloseNavMenu();
                            navigate(page.redirectTo);
                          }}
                          sx={{
                            my: 2,
                            display: "block",
                            px: 3,
                            fontWeight:
                              location.pathname === page.redirectTo
                                ? 500
                                : "normal",
                            color:
                              location.pathname === page.redirectTo
                                ? CGI_GREY
                                : CGI_WHITE,
                          }}
                        >
                          {page.title}
                        </Button>
                      </Box>
                    </Box>
                  ))}
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  justifyContent: "flex-end",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Select
                  sx={{
                    borderRadius: 20,
                    height: 30,
                    textTransform: "uppercase",
                    color: CGI_WHITE,
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: CGI_WHITE,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: CGI_WHITE,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: CGI_WHITE,
                    },
                    ".MuiSvgIcon-root ": {
                      fill: CGI_WHITE + " !important",
                    },
                  }}
                  value={language}
                  onChange={(e) => {
                    setLanguage(e.target.value);
                    i18n.changeLanguage(e.target.value);
                  }}
                >
                  {Object.values(languages).map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {lang}
                    </MenuItem>
                  ))}
                </Select>
                <Divider
                  flexItem
                  orientation="vertical"
                  sx={{ borderColor: CGI_WHITE, mx: 3, my: 0.75 }}
                />
                <Tooltip title={t("profile")}>
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    {profileImg === "" ? (
                      <Avatar sx={{ bgcolor: CGI_RED }}>
                        <PersonIcon sx={{ color: "#CBC3E6" }} />
                      </Avatar>
                    ) : (
                      <Avatar alt="profile_img" src={profileImg} />
                    )}
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: "45px" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem sx={{ pointerEvents: "none" }}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography sx={{ userSelect: "none" }}>
                        {`${t("loggedInAs")}: `}
                      </Typography>
                      <Typography
                        textAlign="center"
                        sx={{ userSelect: "none" }}
                      >
                        {email}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      handleCloseUserMenu();
                      navigate("/account");
                    }}
                  >
                    <Typography textAlign="center">Account</Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={handleSignOut}
                  >
                    <Typography textAlign="center">Logout</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      )}
    </>
  );
}
